import { ColumnDefinitionFactory } from '../../core/service/factory/column-definition-factory.service';
import { ColumnVisibility } from '../../core/model/column-definition';
import { FilterDefinitionItem } from '../../core/model/filter-definition-item';
import { CustomProperty } from '../model/custom-property';
import { CustomPropertyDataType, CustomPropertyVisibilityType } from '../model/const/enums';
import { LocalDate } from '../../core/model/local-date';
import { Injectable } from '@angular/core';
import { AllowSort } from '../../core/service/factory/allow-sort';
import { LanguageService } from '../../core/service/language.service';

@Injectable()
export class CustomPropertyHelper {
  constructor(private languageService: LanguageService, private columnDefinitionFactory: ColumnDefinitionFactory) {}
  /**
   * Maps custom property data types to their respective data prefixes.
   */
  rentalObjectCustomPropertyPrefixMap: Record<CustomPropertyDataType, string> = {
    [CustomPropertyDataType.Text]: 'rentalObjectCustomPropertiesTextValues',
    [CustomPropertyDataType.Date]: 'rentalObjectCustomPropertiesDateValues',
    [CustomPropertyDataType.Numeric]: 'rentalObjectCustomPropertiesNumericValues',
    [CustomPropertyDataType.Boolean]: 'rentalObjectCustomPropertiesBooleanValues',
    [CustomPropertyDataType.Selection]: 'rentalObjectCustomPropertiesTextValues',
  };

  /**
   * Maps rental objects company custom property data types to their respective data prefixes.
   */
  companyCustomPropertyPrefixMap: Record<CustomPropertyDataType, string> = {
    [CustomPropertyDataType.Text]: 'companyCustomPropertiesTextValues',
    [CustomPropertyDataType.Date]: 'companyCustomPropertiesDateValues',
    [CustomPropertyDataType.Numeric]: 'companyCustomPropertiesNumericValues',
    [CustomPropertyDataType.Boolean]: 'companyCustomPropertiesBooleanValues',
    [CustomPropertyDataType.Selection]: 'companyCustomPropertiesTextValues',
  };

  propertyCustomPropertyPrefixMap: Record<CustomPropertyDataType, string> = {
    [CustomPropertyDataType.Text]: 'propertyCustomPropertiesTextValues',
    [CustomPropertyDataType.Date]: 'propertyCustomPropertiesDateValues',
    [CustomPropertyDataType.Numeric]: 'propertyCustomPropertiesNumericValues',
    [CustomPropertyDataType.Boolean]: 'propertyCustomPropertiesBooleanValues',
    [CustomPropertyDataType.Selection]: 'propertyCustomPropertiesTextValues',
  };

  buildingCustomPropertyPrefixMap: Record<CustomPropertyDataType, string> = {
    [CustomPropertyDataType.Text]: 'buildingCustomPropertiesTextValues',
    [CustomPropertyDataType.Date]: 'buildingCustomPropertiesDateValues',
    [CustomPropertyDataType.Numeric]: 'buildingCustomPropertiesNumericValues',
    [CustomPropertyDataType.Boolean]: 'buildingCustomPropertiesBooleanValues',
    [CustomPropertyDataType.Selection]: 'buildingCustomPropertiesTextValues',
  };

  /**
   * Dynamically creates column definitions for custom properties.
   */
  createCustomPropertyColumnDefinition(
    customProperties: CustomProperty[],
    columnDefinitionArray: any[],
    customPropertyVisibilityType: CustomPropertyVisibilityType
  ): void {
    for (const customProperty of customProperties) {
      if (!this.ShowCustomProperty(customProperty, customPropertyVisibilityType)) continue;

      if (customProperty.availableOnDwelling && this.ShowDwellingCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.rentalObjectCustomPropertyPrefixMap[customProperty.dataType];
        const propertyPath = `${propertyPrefix}.${customProperty.id}`;
      const caption = customProperty.caption + ' (' + this.languageService.instant('COMMON.DWELLING') + ')';

        this.CreateColumns(customProperty, columnDefinitionArray, caption, propertyPath);
      }

      if (customProperty.availableOnPremises && this.ShowPremisesCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.rentalObjectCustomPropertyPrefixMap[customProperty.dataType];
        const propertyPath = `${propertyPrefix}.${customProperty.id}`;
        const caption = customProperty.caption + ' (' + this.languageService.instant('CONST.RENTAL_OBJECT_CATEGORY_PREMISES') + ')';

        this.CreateColumns(customProperty, columnDefinitionArray, caption, propertyPath);
      }

      if (customProperty.availableOnCompany && this.ShowCompanyCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.companyCustomPropertyPrefixMap[customProperty.dataType];
        const propertyPath = `${propertyPrefix}.${customProperty.id}`;
      const caption = customProperty.caption + ' (' + this.languageService.instant('COMMON.COMPANY') + ')';

        this.CreateColumns(customProperty, columnDefinitionArray, caption, propertyPath);
      }

      if (customProperty.availableOnProperty && this.ShowPropertyCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.propertyCustomPropertyPrefixMap[customProperty.dataType];
        const propertyPath = `${propertyPrefix}.${customProperty.id}`;
        const caption = customProperty.caption + ' (' + this.languageService.instant('COMMON.PROPERTY') + ')';

        this.CreateColumns(customProperty, columnDefinitionArray, caption, propertyPath);
      }

      if (customProperty.availableOnBuilding && this.ShowBuildingCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.buildingCustomPropertyPrefixMap[customProperty.dataType];
        const propertyPath = `${propertyPrefix}.${customProperty.id}`;
        const caption = customProperty.caption + ' (' + this.languageService.instant('COMMON.BUILDING') + ')';

        this.CreateColumns(customProperty, columnDefinitionArray, caption, propertyPath);
      }
    }
  }

  private CreateColumns(customProperty: CustomProperty, columnDefinitionArray: any[], caption: string, propertyPath: string) {
    if (customProperty.dataType === CustomPropertyDataType.Date) {
      columnDefinitionArray.push(
        this.columnDefinitionFactory.createDate(caption, propertyPath, { visibility: ColumnVisibility.optional, allowSort: AllowSort.No })
      );
    } else if (customProperty.dataType === CustomPropertyDataType.Boolean) {
      columnDefinitionArray.push(
        this.columnDefinitionFactory.createBoolean(caption, propertyPath, {
          visibility: ColumnVisibility.optional,
          allowSort: AllowSort.No,
        })
      );
    } else if (customProperty.dataType === CustomPropertyDataType.Numeric) {
      columnDefinitionArray.push(
        this.columnDefinitionFactory.createDecimal(caption, propertyPath, {
          visibility: ColumnVisibility.optional,
          allowSort: AllowSort.No,
        })
      );
    } else {
      columnDefinitionArray.push(
        this.columnDefinitionFactory.create(caption, propertyPath, { visibility: ColumnVisibility.optional, allowSort: AllowSort.No })
      );
    }
  }

  /**
   * Builds filter definitions from custom properties.
   */

  generateCustomPropertyFilters(
    customProperties: CustomProperty[],
    definition: any,
    customPropertyVisibilityType: CustomPropertyVisibilityType
  ): FilterDefinitionItem[] {
    const filters: FilterDefinitionItem[] = [];

    for (const customProperty of customProperties) {
      if (!this.ShowCustomProperty(customProperty, customPropertyVisibilityType)) continue;

      let value = null;
      let itemsSource = null;

      // Rental-object custom properties are specific to the selected rental-object category.
      if (customProperty.availableOnDwelling && this.ShowDwellingCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.rentalObjectCustomPropertyPrefixMap[customProperty.dataType];
        const name = `${propertyPrefix}_${customProperty.id}`;

        ({ value, itemsSource } = this.CreateFilter(customProperty, definition, value, itemsSource, this.rentalObjectCustomPropertyPrefixMap));

        filters.push(
          new FilterDefinitionItem(
          customProperty.caption + ' (' + this.languageService.instant('COMMON.DWELLING') + ')',
            name,
            value,
            itemsSource,
            definition.selectedFields?.includes(name)
          )
        );
      }

      if (customProperty.availableOnPremises && this.ShowPremisesCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.rentalObjectCustomPropertyPrefixMap[customProperty.dataType];
        const name = `${propertyPrefix}_${customProperty.id}`;

        ({ value, itemsSource } = this.CreateFilter(customProperty, definition, value, itemsSource, this.rentalObjectCustomPropertyPrefixMap));

        filters.push(
          new FilterDefinitionItem(
            customProperty.caption + ' (' + this.languageService.instant('CONST.RENTAL_OBJECT_CATEGORY_PREMISES') + ')',
            name,
            value,
            itemsSource,
            definition.selectedFields?.includes(name)
          )
        );
      }

      if (customProperty.availableOnCompany && this.ShowCompanyCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.companyCustomPropertyPrefixMap[customProperty.dataType];
        const name = `${propertyPrefix}_${customProperty.id}`;

        ({ value, itemsSource } = this.CreateFilter(customProperty, definition, value, itemsSource, this.companyCustomPropertyPrefixMap));

        filters.push(
          new FilterDefinitionItem(
          customProperty.caption + ' (' + this.languageService.instant('COMMON.COMPANY') + ')',
            name,
            value,
            itemsSource,
            definition.selectedFields?.includes(name)
          )
        );
      }

      if (customProperty.availableOnProperty && this.ShowPropertyCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.propertyCustomPropertyPrefixMap[customProperty.dataType];
        const name = `${propertyPrefix}_${customProperty.id}`;

        ({ value, itemsSource } = this.CreateFilter(customProperty, definition, value, itemsSource, this.propertyCustomPropertyPrefixMap));

        filters.push(
          new FilterDefinitionItem(
            customProperty.caption + ' (' + this.languageService.instant('COMMON.PROPERTY') + ')',
            name,
            value,
            itemsSource,
            definition.selectedFields?.includes(name)
          )
        );
      }

      if (customProperty.availableOnBuilding && this.ShowBuildingCustomProperty(customPropertyVisibilityType)) {
        const propertyPrefix = this.buildingCustomPropertyPrefixMap[customProperty.dataType];
        const name = `${propertyPrefix}_${customProperty.id}`;

        ({ value, itemsSource } = this.CreateFilter(customProperty, definition, value, itemsSource, this.buildingCustomPropertyPrefixMap));

        filters.push(
          new FilterDefinitionItem(
            customProperty.caption + ' (' + this.languageService.instant('COMMON.BUILDING') + ')',
            name,
            value,
            itemsSource,
            definition.selectedFields?.includes(name)
          )
        );
      }
    }

    return filters;
  }

  private CreateFilter(
    customProperty: CustomProperty,
    definition: any,
    value: any,
    itemsSource: any,
    prefixMap: Record<CustomPropertyDataType, string>
  ) {
    const values = definition[prefixMap[customProperty.dataType]];

    if (customProperty.dataType === CustomPropertyDataType.Text) {
      const found = values?.find(x => x.key === customProperty.id);
      value = found ? found.value : '';
    } else if (customProperty.dataType === CustomPropertyDataType.Numeric) {
      const found = values?.find(x => x.key === customProperty.id);
      value = found ? found.value : '';
    } else if (customProperty.dataType === CustomPropertyDataType.Date) {
      const found = values?.find(x => x.key === customProperty.id);
      value = found ? LocalDate.fromDate(found.value) : LocalDate.None;
    } else if (customProperty.dataType === CustomPropertyDataType.Selection) {
      const found = values?.find(x => x.key === customProperty.id);
      value = found ? found.value : '';
      itemsSource = customProperty.selectableValues.split('\n').map(x => ({ caption: x, value: x }));
    } else if (customProperty.dataType === CustomPropertyDataType.Boolean) {
      const found = values?.find(x => x.key === customProperty.id);
      value = found ? (found.value === 1 ? true : false) : 'ALL';
      itemsSource = [
        { caption: 'ALL_TEXT', value: 'ALL' },
        { caption: 'YES_TEXT', value: true },
        { caption: 'NO_TEXT', value: false },
      ];
    }
    return { value, itemsSource };
  }

  /**
   * Populates `filterState` with properly structured custom property filter values.
   */
  applyCustomPropertyFilters(filterState: any): void {
    //Company custom properties
    filterState.CompanyCustomPropertiesTextValues = this.extractCustomProperties(filterState, 'companyCustomPropertiesTextValues_', 'text');
    filterState.CompanyCustomPropertiesNumericValues = this.extractCustomProperties(
      filterState,
      'companyCustomPropertiesNumericValues_',
      'numeric'
    );
    filterState.CompanyCustomPropertiesDateValues = this.extractCustomProperties(filterState, 'companyCustomPropertiesDateValues_', 'date');
    filterState.CompanyCustomPropertiesBooleanValues = this.extractCustomProperties(
      filterState,
      'companyCustomPropertiesBooleanValues_',
      'boolean'
    );
    //Rental object custom properties
    filterState.RentalObjectCustomPropertiesTextValues = this.extractCustomProperties(
      filterState,
      'rentalObjectCustomPropertiesTextValues_',
      'text'
    );
    filterState.RentalObjectCustomPropertiesNumericValues = this.extractCustomProperties(
      filterState,
      'rentalObjectCustomPropertiesNumericValues_',
      'numeric'
    );
    filterState.RentalObjectCustomPropertiesDateValues = this.extractCustomProperties(
      filterState,
      'rentalObjectCustomPropertiesDateValues_',
      'date'
    );
    filterState.RentalObjectCustomPropertiesBooleanValues = this.extractCustomProperties(
      filterState,
      'rentalObjectCustomPropertiesBooleanValues_',
      'boolean'
    );
    //Property custom properties
    filterState.PropertyCustomPropertiesTextValues = this.extractCustomProperties(filterState, 'propertyCustomPropertiesTextValues_', 'text');
    filterState.PropertyCustomPropertiesNumericValues = this.extractCustomProperties(
      filterState,
      'propertyCustomPropertiesNumericValues_',
      'numeric'
    );
    filterState.PropertyCustomPropertiesDateValues = this.extractCustomProperties(filterState, 'propertyCustomPropertiesDateValues_', 'date');
    filterState.PropertyCustomPropertiesBooleanValues = this.extractCustomProperties(
      filterState,
      'propertyCustomPropertiesBooleanValues_',
      'boolean'
    );
    //Building custom properties
    filterState.BuildingCustomPropertiesTextValues = this.extractCustomProperties(filterState, 'buildingCustomPropertiesTextValues_', 'text');
    filterState.BuildingCustomPropertiesNumericValues = this.extractCustomProperties(
      filterState,
      'buildingCustomPropertiesNumericValues_',
      'numeric'
    );
    filterState.BuildingCustomPropertiesDateValues = this.extractCustomProperties(filterState, 'buildingCustomPropertiesDateValues_', 'date');
    filterState.BuildingCustomPropertiesBooleanValues = this.extractCustomProperties(
      filterState,
      'buildingCustomPropertiesBooleanValues_',
      'boolean'
    );
  }

  // Helper method to extract custom properties from the filter state
  extractCustomProperties(
    filterState: any,
    prefix: string,
    type: 'text' | 'numeric' | 'date' | 'boolean'
  ): { key: string; value: FilterValue }[] {
    const keys = Object.keys(filterState).filter(p => p.startsWith(prefix));
    return keys
      .map(key => {
        // Extract the GUID from the key by removing the prefix part of the string
        const guid = key.split('_')[1];
        let value = filterState[key];

        switch (type) {
          case 'numeric':
            value = value ? Number(value) : null;
            return { key: guid, value };
          case 'date':
            return { key: guid, value: value?.year ? value : null };
          case 'boolean':
            return { key: guid, value: value === 'ALL' ? null : value };
          default:
            return { key: guid, value };
        }
      })
      .filter(entry => {
        // Only include entries with non-null and valid values
        if (type === 'numeric') return entry.value !== null && !isNaN(entry.value as number);
        return true;
      });
  }

  ShowCustomProperty(customProperty: CustomProperty, customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return (
      (customProperty.availableOnCompany && this.ShowCompanyCustomProperty(customPropertyVisibilityType)) ||
      (customProperty.availableOnDwelling && this.ShowDwellingCustomProperty(customPropertyVisibilityType)) ||
      (customProperty.availableOnPremises && this.ShowPremisesCustomProperty(customPropertyVisibilityType)) ||
      (customProperty.availableOnProperty && this.ShowPropertyCustomProperty(customPropertyVisibilityType)) ||
      (customProperty.availableOnBuilding && this.ShowBuildingCustomProperty(customPropertyVisibilityType))
    );
  }

  private HasVisibility(
    customPropertyVisibilityType: CustomPropertyVisibilityType,
    visibilityType: CustomPropertyVisibilityType
  ): boolean {
    return (customPropertyVisibilityType & visibilityType) === visibilityType;
  }

  private ShowCompanyCustomProperty(customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return this.HasVisibility(customPropertyVisibilityType, CustomPropertyVisibilityType.Company);
  }

  private ShowDwellingCustomProperty(customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return this.HasVisibility(customPropertyVisibilityType, CustomPropertyVisibilityType.Dwelling);
  }

  private ShowPremisesCustomProperty(customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return this.HasVisibility(customPropertyVisibilityType, CustomPropertyVisibilityType.Premises);
  }

  private ShowPropertyCustomProperty(customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return this.HasVisibility(customPropertyVisibilityType, CustomPropertyVisibilityType.Property);
  }

  private ShowBuildingCustomProperty(customPropertyVisibilityType: CustomPropertyVisibilityType): boolean {
    return this.HasVisibility(customPropertyVisibilityType, CustomPropertyVisibilityType.Building);
  }
}

type FilterValue = string | number | boolean | { year?: number } | null;
